import {
  AdAnalyticsFilter,
  AdAnalyticsPlacementTypes,
  AdAnalyticsVideo,
  AdAnalyticsVideoFilter,
  AdAnalyticsVideoSort,
} from '@kaizenplatform/hugot-entity';
import { History } from 'history';
import { List } from 'immutable';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import CreativePerformanceBrowsingAd from '../entity/CreativePerformanceBrowsingAd';
import CreativePerformanceBrowsingCampaign from '../entity/CreativePerformanceBrowsingCampaign';
import CreativePerformanceBrowsingCreative from '../entity/CreativePerformanceBrowsingCreative';
import WithTotalCountArrayPageInfo from '../entity/WithTotalCountArrayPageInfo';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import AdAnalyticsPlacementTypesWebApi from '../service/persistent/AdAnalyticsPlacementTypesWebApi';
import AdAnalyticsVideoWebApi from '../service/persistent/AdAnalyticsVideoWebApi';
import CreativePerformanceCreativeWebApi from '../service/persistent/CreativePerformanceCreativeWebApi';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import {
  disposeAdAnalyticsPlacementTypes,
  finishFetchingAdAnalyticsPlacementTypes,
  setAdAnalyticsPlacementTypes,
  startFetchingAdAnalyticsPlacementTypes,
  updateSelectedPlacementTypes,
} from '../thunk/adAnalyticsPlacementTypes';
import {
  disposeAdAnalyticsVideos,
  finishFetchingAdAnalyticsVideos,
  setAdAnalyticsVideos,
  startFetchingAdAnalyticsVideos,
  updateSort,
} from '../thunk/adAnalyticsVideo';
import {
  appendAdAnalyticsVideos,
  finishFetchingAppendAdAnalyticsVideos,
  setCurrentPage,
  startFetchingAppendAdAnalyticsVideos,
  updateFilters,
  updateSelectedVideos,
} from '../thunk/adAnalyticsVideo';
import {
  disposeCreativePerformanceCreatives,
  finishFetchingCreativePerformanceCreatives,
  setCreativePerformanceAds,
  setCreativePerformanceCampaigns,
  setCreativePerformanceCreatives,
} from '../thunk/creativePerformanceCreative';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';

class ListAdAnalyticsVideoUsecase {
  private readonly store: Store<State>;
  private readonly errorPresenter: ErrorPresenter;
  private readonly adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
  private readonly adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;
  private readonly creativePerformanceCreativeWebApi: CreativePerformanceCreativeWebApi;
  private readonly history: History;
  private readonly organizationWebApi: OrganizationWebApi;

  constructor({
    store,
    history,
    errorPresenter,
    adAnalyticsVideoWebApi,
    adAnalyticsPlacementTypesWebApi,
    creativePerformanceCreativeWebApi,
    organizationWebApi,
  }: {
    store: Store<State>;
    history: History;
    errorPresenter: ErrorPresenter;
    adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
    adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;
    creativePerformanceCreativeWebApi: CreativePerformanceCreativeWebApi;
    organizationWebApi: OrganizationWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.errorPresenter = errorPresenter;
    this.adAnalyticsVideoWebApi = adAnalyticsVideoWebApi;
    this.adAnalyticsPlacementTypesWebApi = adAnalyticsPlacementTypesWebApi;
    this.creativePerformanceCreativeWebApi = creativePerformanceCreativeWebApi;
    this.organizationWebApi = organizationWebApi;
  }

  public prepare = async (): Promise<void> => {
    this.store.dispatch(startFetchingAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsPlacementTypes());
    this.store.dispatch(disposeCreativePerformanceCreatives());

    const query = parse(this.store.getState().location.search.slice(1));
    const organizationId = query.org || null;
    const creativeID: string[] = query.creative ? query.creative.split(',') : [];

    // let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };
    let creatives: {
      creativePerformanceBrowsingCreatives: List<CreativePerformanceBrowsingCreative>;
      pageInfo: WithTotalCountArrayPageInfo;
    };
    let campaigns: {
      creativePerformanceBrowsingCampaigns: List<CreativePerformanceBrowsingCampaign>;
      pageInfo: WithTotalCountArrayPageInfo;
    };
    let ads: {
      creativePerformanceBrowsingAds: List<CreativePerformanceBrowsingAd>;
      pageInfo: WithTotalCountArrayPageInfo;
    };
    // let concatAnalyticsVideos: List<AdAnalyticsVideo>;
    let concatCreativePerformanceCreatives: List<CreativePerformanceBrowsingCreative>;
    let concatCreativePerformanceCampaigns: List<CreativePerformanceBrowsingCampaign>;
    let concatCreativePerformanceAds: List<CreativePerformanceBrowsingAd>;
    let placementTypes: AdAnalyticsPlacementTypes;
    const perPage = this.store.getState().adAnalyticsVideos.perPage;
    try {
      if (organizationId) {
        const organization = await this.organizationWebApi.getById(organizationId);
        this.store.dispatch(setLastAccessedOrganization(organization));
      }
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
      return;
    }

    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };
    const filters = this.store.getState().adAnalyticsVideos.filters;
    const sort = this.store.getState().adAnalyticsVideos.sort;

    try {
      placementTypes = await this.featchAdAnalyticsPlacementTypes();
      // items = await this.fetchAdAnalyticsVideos({
      //   ...properties,
      //   filters,
      //   perPage,
      //   sort: sort ? sort : undefined,
      //   ...{},
      // });
      // concatAnalyticsVideos = items.adAnalyticsVideos;
      creatives = await this.fetchAdCreativePerformanceCreatives({
        ...properties,
        filters,
        perPage,
        sort: sort ? sort : undefined,
        ...{},
      });
      concatCreativePerformanceCreatives = creatives.creativePerformanceBrowsingCreatives;
      campaigns = await this.fetchAdCreativePerformanceCampaigns({
        ...properties,
        filters,
        perPage,
        sort: sort ? sort : undefined,
        ...{},
      });
      concatCreativePerformanceCampaigns = campaigns.creativePerformanceBrowsingCampaigns;
      ads = await this.fetchAdCreativePerformanceAds({
        ...properties,
        filters,
        perPage,
        sort: sort ? sort : undefined,
        ...{},
      });
      concatCreativePerformanceAds = ads.creativePerformanceBrowsingAds;
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.store.dispatch(finishFetchingCreativePerformanceCreatives());
      this.store.dispatch(disposeAdAnalyticsVideos());
      this.store.dispatch(disposeCreativePerformanceCreatives());
      this.errorPresenter.showAsFatal(err);
      return;
    }
    if (creativeID.length > 0) {
      try {
        // const specifiedCreative = await this.fetchAdAnalyticsVideos({ adVideoIds: creativeID });
        // specifiedCreative.adAnalyticsVideos.forEach((video) => {
        //   this.addSelectedVideosFromParams(video);
        //   const filterdItems = items.adAnalyticsVideos.filter((adVideo) => adVideo.adVideoId !== video.adVideoId);
        //   concatAnalyticsVideos = filterdItems.unshift(video);
        // });
      } catch (err) {
        this.store.dispatch(finishFetchingAdAnalyticsVideos());
        this.store.dispatch(disposeAdAnalyticsVideos());
        this.errorPresenter.showAsFatal(err);
        return;
      }
    }

    // this.store.dispatch(setAdAnalyticsVideos(concatAnalyticsVideos, items.pageInfo));
    this.store.dispatch(setAdAnalyticsPlacementTypes(placementTypes));
    this.store.dispatch(setCreativePerformanceCreatives(concatCreativePerformanceCreatives, creatives.pageInfo));
    this.store.dispatch(setCreativePerformanceCampaigns(concatCreativePerformanceCampaigns, campaigns.pageInfo));
    this.store.dispatch(setCreativePerformanceAds(concatCreativePerformanceAds, ads.pageInfo));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
    this.store.dispatch(finishFetchingAdAnalyticsPlacementTypes());
    this.store.dispatch(finishFetchingCreativePerformanceCreatives());
  };

  public loadMoreVideos = async (cursor: string): Promise<void> => {
    this.store.dispatch(startFetchingAppendAdAnalyticsVideos());
    const filters = this.store.getState().adAnalyticsVideos.filters;
    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };
    const sort = this.store.getState().adAnalyticsVideos.sort;

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };
    try {
      items = await this.fetchAdAnalyticsVideos({
        ...properties,
        filters,
        after: cursor,
        perPage: 50,
        sort: sort ? sort : undefined,
        ...{},
      });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(appendAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(finishFetchingAppendAdAnalyticsVideos());
  };

  public updatePlacementTypes = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
  }: AdAnalyticsFilter): Promise<void> => {
    this.store.dispatch(setCurrentPage(0));

    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: platformKey ? platformKey : selectedPlacementTypes.platformKey,
      mediaKey: mediaKey ? mediaKey : selectedPlacementTypes.mediaKey,
      placementKey: placementKey ? placementKey : selectedPlacementTypes.placementKey,
      period: period ? period : selectedPlacementTypes.period,
    };

    this.store.dispatch(updateSelectedPlacementTypes(properties));
  };

  public submitPlacementTypes = async (): Promise<void> => {
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsVideos());
    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };
    const filters = this.store.getState().adAnalyticsVideos.filters;
    const perPage = this.store.getState().adAnalyticsVideos.perPage;
    const sort = this.store.getState().adAnalyticsVideos.sort;

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };

    try {
      items = await this.fetchAdAnalyticsVideos({
        ...properties,
        filters,
        perPage,
        sort: sort ? sort : undefined,
        ...{},
      });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }
    this.store.dispatch(setAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
  };

  public fetchAdAnalyticsVideos = async ({
    platformKey,
    mediaKey,
    placementKey,
    perPage,
    sort,
    period = 'lifetime',
    adVideoIds = null,
    filters = [],
    after = '',
    before = '',
  }: AdAnalyticsFilter & {
    adVideoIds?: string[] | null;
    filters?: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
  }): Promise<{ adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo }> => {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));
    return await this.adAnalyticsVideoWebApi.getAllOfAdAnalyticsVideos({
      platformKey,
      mediaKey,
      placementKey,
      period,
      adVideoIds,
      filters,
      sort,
      perPage,
      after,
      before,
      organizationId: organization ? organization.id : '',
    });
  };

  public featchAdAnalyticsPlacementTypes = async (): Promise<AdAnalyticsPlacementTypes> =>
    await this.adAnalyticsPlacementTypesWebApi.getAdAnalyticsPlacementTypes();

  public fetchAdCreativePerformanceCreatives = async ({
    platformKey,
    mediaKey,
    placementKey,
    perPage,
    sort,
    period = 'lifetime',
    filters = [],
    after = '',
    before = '',
  }: AdAnalyticsFilter & {
    filters?: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
  }): Promise<{
    creativePerformanceBrowsingCreatives: List<CreativePerformanceBrowsingCreative>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    // this.store.dispatch(setLastAccessedOrganization(organization));
    return await this.creativePerformanceCreativeWebApi.creatives({
      platformKey,
      mediaKey,
      placementKey,
      period,
      filters,
      sort,
      perPage,
      after,
      before,
      organizationId: organization ? organization.id : '',
    });
  };

  public fetchAdCreativePerformanceCampaigns = async ({
    platformKey,
    mediaKey,
    placementKey,
    perPage,
    sort,
    period = 'lifetime',
    filters = [],
    after = '',
    before = '',
  }: AdAnalyticsFilter & {
    filters?: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
  }): Promise<{
    creativePerformanceBrowsingCampaigns: List<CreativePerformanceBrowsingCampaign>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    // this.store.dispatch(setLastAccessedOrganization(organization));
    return await this.creativePerformanceCreativeWebApi.campaigns({
      platformKey,
      mediaKey,
      placementKey,
      period,
      filters,
      sort,
      perPage,
      after,
      before,
      organizationId: organization ? organization.id : '',
    });
  };

  public fetchAdCreativePerformanceAds = async ({
    platformKey,
    mediaKey,
    placementKey,
    perPage,
    sort,
    period = 'lifetime',
    filters = [],
    after = '',
    before = '',
  }: AdAnalyticsFilter & {
    filters?: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
  }): Promise<{
    creativePerformanceBrowsingAds: List<CreativePerformanceBrowsingAd>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    // this.store.dispatch(setLastAccessedOrganization(organization));
    return await this.creativePerformanceCreativeWebApi.ads({
      platformKey,
      mediaKey,
      placementKey,
      period,
      filters,
      sort,
      perPage,
      after,
      before,
      organizationId: organization ? organization.id : '',
    });
  };

  public addSelectedVideosFromParams = (selectedVideo: AdAnalyticsVideo | undefined) => {
    if (!selectedVideo) {
      return;
    }
    const selectedViodes = this.store.getState().adAnalyticsVideos.selectedVideos;
    let videos = List<AdAnalyticsVideo>();
    if (selectedViodes.findIndex((existVideo) => existVideo.adVideoId === selectedVideo.adVideoId) !== -1) {
      return;
    } else {
      videos = List([...selectedViodes, selectedVideo]);
    }
    this.store.dispatch(updateSelectedVideos(videos));
  };

  public updateSelectedViodes = (selectedVideo: AdAnalyticsVideo | undefined) => {
    if (!selectedVideo) {
      return;
    }
    const selectedViodes = this.store.getState().adAnalyticsVideos.selectedVideos;
    let videos = List<AdAnalyticsVideo>();
    if (selectedViodes.findIndex((existVideo) => existVideo.adVideoId === selectedVideo.adVideoId) !== -1) {
      videos = selectedViodes.filter(
        (existVideo: AdAnalyticsVideo) => existVideo.adVideoId !== selectedVideo.adVideoId,
      );
    } else {
      videos = List([...selectedViodes, selectedVideo]);
    }
    this.store.dispatch(updateSelectedVideos(videos));
  };

  public updateFilters = ({ metrics, min, max }: AdAnalyticsVideoFilter) => {
    const filters = this.store.getState().adAnalyticsVideos.filters;
    const index = filters.findIndex((filter) => filter.metrics === metrics);
    let newFilters = [];

    // 既に対象のmetricsのfilterが存在すれば上書きする
    if (index !== -1) {
      newFilters = filters.map((filter) =>
        filter.metrics === metrics
          ? {
              min: min !== undefined ? min : filter.min,
              max: max !== undefined ? max : filter.max,
              metrics,
            }
          : filter,
      );
    } else {
      // なければ新しく追加
      const filter: AdAnalyticsVideoFilter = {
        min: min !== undefined ? min : null,
        max: max !== undefined ? max : null,
        metrics,
      };
      newFilters = [...filters, filter];
    }

    this.store.dispatch(updateFilters(newFilters));
  };

  public submitFilters = async (newFilters: AdAnalyticsVideoFilter[]) => {
    this.store.dispatch(updateFilters(newFilters));
    this.store.dispatch(setCurrentPage(0));
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsVideos());

    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };

    const filters = this.store.getState().adAnalyticsVideos.filters;
    const perPage = this.store.getState().adAnalyticsVideos.perPage;
    const sort = this.store.getState().adAnalyticsVideos.sort;

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };

    try {
      items = await this.fetchAdAnalyticsVideos({
        ...properties,
        filters,
        perPage,
        sort: sort ? sort : undefined,
        ...{},
      });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(setAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
  };

  public updateSort = async (sort: AdAnalyticsVideoSort) => {
    this.store.dispatch(updateSort(sort));
    this.store.dispatch(setCurrentPage(0));
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsVideos());

    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };

    const filters = this.store.getState().adAnalyticsVideos.filters;
    const perPage = this.store.getState().adAnalyticsVideos.perPage;

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };

    try {
      items = await this.fetchAdAnalyticsVideos({
        ...properties,
        filters,
        perPage,
        sort,
        ...{},
      });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(setAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
  };

  public goAnalyticsPage = (idList: string[]) => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    const orgId = organization ? organization.id : '';
    this.history.push(`/analytics/${orgId !== '' ? `?org=${orgId}&` : '?'}creative=${idList.join(',')}`);
  };

  public goReportListPage = () => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    const orgId = organization ? organization.id : '';
    this.history.push(`/ad-creatives-report${orgId !== '' ? `?org=${orgId}` : ''}`);
  };

  public dispose = (): void => {
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(disposeAdAnalyticsPlacementTypes());
  };

  // Mock
  // ---------------------------------------------------------
  public fetchAdAnalyticsVideosMock = async (): Promise<{
    adAnalyticsVideos: List<AdAnalyticsVideo>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => await this.adAnalyticsVideoWebApi.getAllOfAdAnalyticsVideoMock();
}

export default ListAdAnalyticsVideoUsecase;
